import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OwnerEntity, SERIAL_SHORT_NEW_RE, SERIAL_LONG_RE } from '@bhops/shared';
import { robotsApi } from '@/api/robots';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, FormError } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import {
  defaultRobotFormValues,
  RobotForm,
  valuesToCreateBody,
  type RobotFormValues,
} from './RobotForm';

interface Props {
  open: boolean;
  onClose: () => void;
}

// v1.0.14 §6 — '새 로봇 추가 안 됨' 버그 수정.
//
// 사용자가 직접 사용 후 "안 된다" 라고 피드백을 줬으므로, 가장 흔한
// 실패 경로 (서버 regex 와 클라이언트 입력 불일치 → 400) 를 클라이언트
// 사이드에서 잡아 명확한 에러 메시지를 보여준다.
//
// 추가 정리:
//   - 입력값 trim (공백 실수 차단)
//   - 빈 문자열을 undefined 로 normalize (zero-width 케이스)
//   - 두 SN 모두 입력된 경우 둘 다 검증
//   - 서버 응답이 ProblemDetails 인 경우 extractApiError 가 한 줄로
//     포매팅 → FormError 가 그대로 표시
function validateValues(v: RobotFormValues): string | null {
  const short = v.serialShort?.trim();
  const long = v.serialLong?.trim();

  if (!short && !long) {
    return 'Short SN 또는 Long SN 중 최소 하나는 입력해야 합니다.';
  }
  if (short && !SERIAL_SHORT_NEW_RE.test(short)) {
    return `Short SN 형식이 맞지 않습니다. 123이나 12-34처럼 숫자 또는 숫자-숫자로 입력해 주세요. 입력한 값: "${short}"`;
  }
  if (long && !SERIAL_LONG_RE.test(long)) {
    return `Long SN 형식이 맞지 않습니다. GEN2-2613-WF207처럼 입력해 주세요. 입력한 값: "${long}"`;
  }
  if (!v.modelCode) {
    return '모델을 선택해 주세요.';
  }
  return null;
}

export function RobotCreateModal({ open, onClose }: Props) {
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const qc = useQueryClient();
  const [values, setValues] = useState<RobotFormValues>(() =>
    defaultRobotFormValues(user?.ownerEntity ?? OwnerEntity.BEYOND_HONEYCOMB),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValues(defaultRobotFormValues(user?.ownerEntity ?? OwnerEntity.BEYOND_HONEYCOMB));
      setError(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () => {
      // v1.0.14 — submit 직전에 trim 처리 한 값을 보낸다. valuesToCreateBody
      // 는 undefined 를 그대로 흘려보내므로 빈 문자열 normalize 도 함께.
      const body = valuesToCreateBody({
        ...values,
        serialShort: values.serialShort?.trim() || undefined,
        serialLong: values.serialLong?.trim() || undefined,
        color: values.color?.trim() || undefined,
        // FB2 §5C — loaderType 제거(구분 사양과 중복).
        notes: values.notes?.trim() || undefined,
      });
      return robotsApi.create(body);
    },
    onSuccess: (robot) => {
      qc.invalidateQueries({ queryKey: ['robots'] });
      // New robot enters lifecycle pipeline (typically IN_PRODUCTION
      // or IN_STORAGE) → stage counts need refresh, otherwise the new
      // robot is hidden from the pipeline for up to 60s.
      qc.invalidateQueries({ queryKey: ['robots-count'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(
        `${robot.serialShort ? `#${robot.serialShort}` : robot.serialLong} 로봇을 등록했습니다.`,
        '등록 완료',
      );
      onClose();
      setValues(defaultRobotFormValues(user?.ownerEntity ?? OwnerEntity.BEYOND_HONEYCOMB));
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleSubmit(): void {
    setError(null);
    const validationError = validateValues(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    create.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 로봇 등록"
      width={560}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="robot-create-form" disabled={create.isPending}>
            {create.isPending ? '저장 중…' : '등록'}
          </Button>
        </>
      }
    >
      <FormError>{error}</FormError>
      <RobotForm
        formId="robot-create-form"
        mode="create"
        values={values}
        onChange={setValues}
        onSubmit={handleSubmit}
      />
    </Modal>
  );
}
